import { quoteIdentityBlurb } from '@/components/quick-study-results/QuickStudyTwinsPanel'
import { Result, TwinResponse } from '@/components/quick-study-results/quickStudyTypes'

import { fillPickedOptionChip, initialsFromDisplayName, StudyDocQuote } from '../enrichStudyDocQuote'
import { PortraitMosaicQuote } from '../PortraitMosaicStrip/PortraitMosaicQuoteBlock'
import { featuredQuestionKeys } from './highlightMagazineStripPersonas'

/**
 * A verbatim shorter than this reads as a stray "yes" / "n/a" rather than a usable
 * open-end answer, so it is dropped rather than surfaced as a clickable quote.
 */
const MIN_ROW_QUOTE_LENGTH = 20

function personaIdFromRow(row: TwinResponse): string {
  return typeof row.persona_id === 'string' ? row.persona_id.trim() : ''
}

/**
 * A respondent's own answer to the featured question, or "" when it is not a usable
 * verbatim. Only a plain string counts — an object (matrix cell payload, "other" shape)
 * or an array is not a quote and is dropped rather than stringified.
 *
 * Tries every key {@link featuredQuestionKeys} returns, in order, and takes the first
 * usable string: `featuredQuestionName` is not always the real `survey_data` field name
 * (see that function's doc comment) — this is the SAME fallback chain
 * `highlightMagazineStripPersonas` already relies on, so a tile that is correctly dimmed
 * "highlighted" already had its answer under `qs_main` even when `featuredQuestionName`
 * itself resolved to an opaque per-exhibit id.
 */
function rowQuoteText(row: TwinResponse, featuredQuestionName: string): string {
  const data = row.survey_data || {}
  for (const key of featuredQuestionKeys(featuredQuestionName)) {
    const raw = data[key]
    if (typeof raw !== 'string') {
      continue
    }
    const text = raw.trim()
    if (text.length >= MIN_ROW_QUOTE_LENGTH) {
      return text
    }
  }
  return ''
}

/**
 * A quote's display name straight from the response's own attributes. Mirrors the
 * private `nameFromAttributes` already duplicated in `enrichStudyDocQuote.ts` and
 * `QuickStudyOpenEndCard.tsx` — kept local for the same reason neither of those exports
 * it: this is a one-line read of a handful of well-known attribute keys, not shared
 * derivation logic.
 */
function nameFromAttributes(attributes: Record<string, unknown> | undefined): string {
  const attrs = attributes ?? {}
  const raw = attrs.display_name ?? attrs.name ?? attrs.persona_name ?? ''
  return String(raw).trim()
}

/**
 * The same "Name, blurb" join `withMagazineBandAttribution` (`StudyDocBody.tsx`) uses
 * for a curated quote. Curated quotes are only ever built from a NAMED respondent
 * (`isNamedStudyDocQuote` drops anything else upstream), so that join always has a real
 * name to anchor on. A row quote has no such gate — every respondent with a usable
 * verbatim gets a tile, including one whose attributes resolve no name at all — so this
 * falls back to the blurb alone, or to nothing, rather than printing an empty name.
 */
function rowQuoteAttribution(name: string, blurb: string): string | undefined {
  if (blurb) {
    return name ? `${name}, ${blurb}` : blurb
  }
  return name || undefined
}

/**
 * One quote per respondent, straight from THEIR OWN answer to the featured open-end
 * question — the "every face speaks" complement to the curated `quote:` bands
 * (`StudyDocBody.tsx`'s `quotesForFeaturedQuestion`). A tile in `PortraitMosaicStrip`
 * becomes clickable exactly when its persona has an entry in the `quotes` array it is
 * given (`quoteIndexByPersonaId`), so the array this returns is the set of respondents
 * whose own verbatim can be shown, not a curated highlight reel.
 *
 * `resultsFeaturedFirst` is every Result embedded in the document, with the band's own
 * featured result first (same ordering `studyDocResultsForChip` already builds for the
 * curated path) — NOT just that result's own `responses`. Live QA (thread
 * 5bfa8a9d-ffd8-4ec2-abec-9285b9fd947a) found the band's tile panel and the open-end
 * verbatims can live on two DIFFERENT results of the same document (both frequently named
 * `qs_main`, the exact ambiguity {@link fillPickedOptionChip}'s doc comment already
 * describes for the cite chip) — restricting this to a single result's rows left every
 * tile inert. Scanning every result, featured-first, and keying by `persona_id` finds a
 * respondent's own answer wherever it actually landed, while still preferring the
 * featured result's own copy when more than one result carries an answer for them.
 *
 * Pure and name-agnostic (hard rule 1): reads `survey_data` (via {@link rowQuoteText}'s
 * key fallback chain) and each response's own `attributes`, never a question id or a
 * hard-coded value. Filters:
 *   - the answer must be a string of length >= {@link MIN_ROW_QUOTE_LENGTH} (drops
 *     blank/short "yes"/"n/a" answers, and any non-string payload);
 *   - one quote per `persona_id`, GLOBALLY across every result given — a monadic result
 *     repeats a respondent once per item, and the first usable row wins, in the order
 *     `resultsFeaturedFirst` and then each result's own `responses` are walked.
 *
 * Attribution is built the same way a curated band quote's is: name + identity blurb
 * (`quoteIdentityBlurb`, the same enrichment `enrichStudyDocQuote.ts` uses) joined
 * "Name, blurb" — see {@link rowQuoteAttribution} for the one deliberate difference
 * (a nameless respondent still gets a quote here, never dropped).
 *
 * `chipResults` is the SAME array `studyDocResultsForChip` builds for the curated path
 * (`StudyDocBody.tsx`) — passed straight to `fillPickedOptionChip` so a row quote's cite
 * can also resolve a "chose X" chip from a sibling choice question in the document.
 */
export function buildRowQuotes(
  resultsFeaturedFirst: Array<Result>,
  featuredQuestionName: string,
  chipResults: Array<Result>,
): Array<PortraitMosaicQuote> {
  const seen = new Set<string>()
  const quotes: Array<PortraitMosaicQuote> = []

  resultsFeaturedFirst.forEach((result) => {
    ;(result.responses || []).forEach((row) => {
      const personaId = personaIdFromRow(row)
      if (!personaId || seen.has(personaId)) {
        return
      }
      const text = rowQuoteText(row, featuredQuestionName)
      if (!text) {
        return
      }
      seen.add(personaId)

      const attributes = (row.attributes ?? {}) as Record<string, unknown>
      const name = nameFromAttributes(attributes)
      const blurb = quoteIdentityBlurb(attributes)

      const draft: StudyDocQuote = {
        text,
        name: name || 'Respondent',
        initials: initialsFromDisplayName(name || 'Respondent'),
        blurb: blurb || undefined,
        attribution: rowQuoteAttribution(name, blurb),
        personaId,
      }

      const filled = fillPickedOptionChip(draft, chipResults)

      quotes.push({
        personaId,
        text: filled.text,
        attribution: filled.attribution,
        versionLabel: filled.versionLabel,
        versionLabelKind: filled.versionLabelKind,
      })
    })
  })

  return quotes
}
